import clsx from 'clsx';
import { InlineAlert } from '../../../helpers/UI/InlineAlert/InlineAlert';
import { Button, Card } from '../../PrizeDraw/components/ui';
import { SKILL_TEST_MAX_ATTEMPTS } from '../skillTestUtils';
import { Timer } from './Timer';

type QuestionCardProps = {
  question: string;
  answerInput: string;
  onAnswerChange: (value: string) => void;
  attestationConfirmed: boolean;
  onAttestationChange: (confirmed: boolean) => void;
  onSubmit: () => void;
  secondsRemaining: number;
  attemptCount: number;
  retryMessage: string | null;
  submitError: string | null;
  onDismissSubmitError: () => void;
  isSubmitting: boolean;
  isLocked: boolean;
};

export function QuestionCard({
  question,
  answerInput,
  onAnswerChange,
  attestationConfirmed,
  onAttestationChange,
  onSubmit,
  secondsRemaining,
  attemptCount,
  retryMessage,
  submitError,
  onDismissSubmitError,
  isSubmitting,
  isLocked,
}: QuestionCardProps) {
  const attemptsRemaining = Math.max(SKILL_TEST_MAX_ATTEMPTS - attemptCount, 0);
  const canSubmit =
    answerInput.trim().length > 0 && attestationConfirmed && !isSubmitting && !isLocked;

  return (
    <Card className='overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.08)]'>
      <div className='border-b border-[#E9EDF5] bg-[#F8FAFF] px-6 py-5'>
        <div className='flex flex-col items-center justify-between gap-4 sm:flex-row'>
          <div className='text-center sm:text-left'>
            <p className='text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]'>
              Skill test in progress
            </p>
            <p className='mt-1 text-[13px] text-[#5B6478]'>
              Attempt {Math.min(attemptCount + 1, SKILL_TEST_MAX_ATTEMPTS)} of{' '}
              {SKILL_TEST_MAX_ATTEMPTS}
              {attemptsRemaining > 0 ? ` · ${attemptsRemaining} remaining` : ''}
            </p>
          </div>
          <Timer secondsRemaining={secondsRemaining} />
        </div>
      </div>

      <div className='space-y-5 p-6'>
        {retryMessage && <InlineAlert type='warning' message={retryMessage} />}

        {submitError && (
          <InlineAlert
            type='error'
            title='Submission failed'
            message={submitError}
            dismissible
            onDismiss={onDismissSubmitError}
          />
        )}

        {isLocked && (
          <InlineAlert
            type='warning'
            message='Time is up. Your answer can no longer be submitted.'
          />
        )}

        <div>
          <p className='mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]'>
            Solve the equation
          </p>
          <h2 className='font-display text-[28px] font-semibold leading-8 text-[#0A0B14]'>
            {question}
          </h2>
        </div>

        <div>
          <label htmlFor='skill-test-answer' className='mb-2 block text-[14px] font-medium text-[#374151]'>
            Your answer
          </label>
          <input
            id='skill-test-answer'
            type='number'
            inputMode='numeric'
            min={1}
            step={1}
            value={answerInput}
            onChange={(event) => onAnswerChange(event.target.value)}
            disabled={isLocked || isSubmitting}
            placeholder='Enter a whole number'
            className={clsx(
              'w-full rounded-[12px] border px-4 py-3 text-[18px] font-semibold tabular-nums text-[#0A0B14] outline-none transition-colors',
              'border-[#E9EDF5] bg-white focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/15',
              (isLocked || isSubmitting) && 'cursor-not-allowed bg-[#F9FAFB] opacity-70'
            )}
          />
        </div>

        <div className='flex items-start gap-3'>
          <input
            id='skill-test-attestation'
            type='checkbox'
            checked={attestationConfirmed}
            onChange={(event) => onAttestationChange(event.target.checked)}
            disabled={isLocked || isSubmitting}
            className='mt-0.5 h-4 w-4 shrink-0 rounded border-[#D1D5DB] text-[#0055FF] focus:ring-[#0055FF]/20 disabled:cursor-not-allowed disabled:opacity-60'
          />
          <label
            htmlFor='skill-test-attestation'
            className='cursor-pointer text-[14px] leading-6 text-[#374151]'
          >
            I confirm I answered this question myself, without any assistance.
          </label>
        </div>

        <Button
          variant='gold'
          className='w-full justify-center py-3 text-[15px] disabled:cursor-not-allowed disabled:opacity-60'
          onClick={onSubmit}
          disabled={!canSubmit}
        >
          {isSubmitting ? 'Submitting…' : 'Submit answer'}
        </Button>
      </div>
    </Card>
  );
}
